import { isFuture } from 'date-fns';
import { ArrayHelper } from 'lib_ts/classes/array.helper';
import { S3Command } from 'lib_ts/enums/data.enums';
import { AwsRegion } from 'lib_ts/enums/s3.enum';
import { DictOf } from 'lib_ts/interfaces/common/i-dictionary';
import { IExpiringUrl, IUrlRequest } from 'lib_ts/interfaces/common/i-url-dict';
import { merge } from 'lodash';
import { MainService } from 'services/main.service';
import { create } from 'zustand';

type SignedUrlsState = {
  // key would be the path or hash of path
  getObjectDict: DictOf<IExpiringUrl>;
};

type SignedUrlsActions = {
  getSignedUrl: (
    region: AwsRegion | undefined,
    path: string,
    action: S3Command,
    force?: boolean,
    checkAvailability?: boolean
  ) => Promise<string | undefined>;

  // always gets signed URLs for each path provided, even if there is a cached value that is not expired
  // merges results into getObjectDict (if action is getObject)
  fetchSignedUrls: (
    payloads: IUrlRequest[],
    action: S3Command,
    checkAvailability?: boolean
  ) => Promise<DictOf<IExpiringUrl>>;
};

type SignedUrlsStore = SignedUrlsState & SignedUrlsActions;

const useSignedUrlsStore = create<SignedUrlsStore>((set, get) => ({
  getObjectDict: {},

  getSignedUrl: async (region, path, action, force, checkAvailability) => {
    switch (action) {
      case S3Command.getObject: {
        // only getObject URLs get cached
        const entry = get().getObjectDict[path];

        if (!force && entry && isFuture(entry.expires)) {
          // use the cached value that hasn't expired yet
          return entry.url;
        }

        // sign a new value
        const tempDict = await MainService.getInstance().signUrls(
          [{ region: region, path: path }],
          action,
          checkAvailability
        );

        const tempEntry = tempDict[path];

        if (!tempEntry) {
          console.error(
            `Failed to find signed URL in result - getObject`,
            tempDict,
            path
          );
          return undefined;
        }

        // cache the value for next time
        set((prev) => ({
          ...prev,
          getObjectDict: {
            ...prev.getObjectDict,
            ...tempDict,
          },
        }));

        return tempEntry.url;
      }

      default: {
        const result = await MainService.getInstance().signUrls(
          [{ region: region, path: path }],
          action
        );

        const tempEntry = result[path];

        if (!tempEntry) {
          console.error(
            `Failed to find signed URL in result - default`,
            result,
            path
          );
          return undefined;
        }

        return tempEntry.url;
      }
    }
  },

  fetchSignedUrls: async (payloads, action, checkAvailability) => {
    const workingDict: DictOf<IExpiringUrl> = {};

    const pathChunks = ArrayHelper.chunkArray(payloads, 50);

    for (const chunk of pathChunks) {
      const tempDict = await MainService.getInstance().signUrls(
        chunk,
        action,
        checkAvailability
      );
      merge(workingDict, tempDict);
      // todo: determine whether we need to do MiscHelper.sleep to give the server a break
    }

    // cache all signed results for getObject
    if (action === S3Command.getObject) {
      const { getObjectDict } = get();

      const nextDict = {
        ...getObjectDict,
        ...workingDict,
      };

      set({ getObjectDict: nextDict });
    }

    return workingDict;
  },
}));

export default useSignedUrlsStore;
